import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import Input from "@/components/ui/input";
import DatePicker from "@/components/ui/datepicker";
import Dropdown from "@/components/ui/dropdown";
import {
  ArrowDown,
  Search as SearchIcon,
} from "@/components/icons";
import {
  certificateHierarchyApi,
  type CertificateHierarchyFilters,
  type Level1Item,
  type Level2Item,
  type Level3Item,
  type Level4Item,
} from "../../services/reports-certificate-hierarchy.api";
import { staffManagementApi } from "@/features/staff-management/services/staff-management.api";
import styles from "./CertificateHierarchyReportPage.module.scss";

type LevelKey =
  | `L1:${string}`
  | `L2:${string}:${string}`
  | `L3:${string}:${string}:${string}`;

interface LevelState {
  loading: boolean;
  items: unknown[];
}

const PAGE_LIMIT = 50;

const CertificateHierarchyReportPage: React.FC = () => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<CertificateHierarchyFilters>({
    page: 1,
    limit: PAGE_LIMIT,
  });
  const [level1, setLevel1] = useState<Level1Item[]>([]);
  const [level1Loading, setLevel1Loading] = useState(false);
  const [departments, setDepartments] = useState<
    { label: string; value: string }[]
  >([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [children, setChildren] = useState<Map<LevelKey, LevelState>>(new Map());

  const fetchLevel1 = useCallback(async () => {
    setLevel1Loading(true);
    try {
      const res = await certificateHierarchyApi.getLevel1(filters);
      setLevel1(res.items ?? []);
    } finally {
      setLevel1Loading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchLevel1();
  }, [fetchLevel1]);

  useEffect(() => {
    staffManagementApi
      .getDepartments()
      .then((list) => {
        setDepartments(list.map((d) => ({ label: d.name, value: d.id })));
      })
      .catch(() => setDepartments([]));
  }, []);

  // Filtre değişince açık tree tamamen sıfırlansın (cache stale olmasın)
  useEffect(() => {
    setExpanded(new Set());
    setChildren(new Map());
  }, [
    filters.dateFrom,
    filters.dateTo,
    filters.search,
    JSON.stringify(filters.departmentIds ?? []),
  ]);

  // ─────────────────────────────────────────────────────────────
  const toggleRow = async (
    rowKey: string,
    loader: () => Promise<unknown[]>,
    cacheKey: LevelKey,
  ) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
    if (children.has(cacheKey)) return; // already loaded
    setChildren((prev) => {
      const next = new Map(prev);
      next.set(cacheKey, { loading: true, items: [] });
      return next;
    });
    try {
      const items = await loader();
      setChildren((prev) => {
        const next = new Map(prev);
        next.set(cacheKey, { loading: false, items });
        return next;
      });
    } catch {
      setChildren((prev) => {
        const next = new Map(prev);
        next.set(cacheKey, { loading: false, items: [] });
        return next;
      });
    }
  };

  // ─────────────────────────────────────────────────────────────
  const renderFilters = () => (
    <div className={styles.filters}>
      <div className={styles.filterItem}>
        <DatePicker
          label={t("reports.filter.dateFrom")}
          value={filters.dateFrom ? new Date(filters.dateFrom) : null}
          onChange={(v) => {
            const d = Array.isArray(v) ? v[0] : v;
            setFilters((f) => ({
              ...f,
              dateFrom:
                d instanceof Date
                  ? d.toISOString().slice(0, 10)
                  : undefined,
            }));
          }}
        />
      </div>
      <div className={styles.filterItem}>
        <DatePicker
          label={t("reports.filter.dateTo")}
          value={filters.dateTo ? new Date(filters.dateTo) : null}
          onChange={(v) => {
            const d = Array.isArray(v) ? v[0] : v;
            setFilters((f) => ({
              ...f,
              dateTo:
                d instanceof Date
                  ? d.toISOString().slice(0, 10)
                  : undefined,
            }));
          }}
        />
      </div>
      {departments.length > 0 && (
        <div className={styles.filterItem}>
          <Dropdown
            label={t("reports.filter.departments")}
            placeholder={t("common.all")}
            options={[{ label: t("common.all"), value: "" }, ...departments]}
            value={filters.departmentIds?.[0] ?? ""}
            onSelect={(v) => {
              const val = Array.isArray(v) ? v[0] : v;
              setFilters((f) => ({
                ...f,
                departmentIds: val ? [val] : undefined,
              }));
            }}
            searchable
          />
        </div>
      )}
      <div className={`${styles.filterItem} ${styles.search}`}>
        <Input
          placeholder={t("reports.certificateHierarchy.searchPlaceholder")}
          value={filters.search ?? ""}
          onChange={(e) =>
            setFilters((f) => ({ ...f, search: e.target.value || undefined }))
          }
          startIcon={<SearchIcon width={16} height={16} />}
        />
      </div>
    </div>
  );

  const renderMetricCell = (value: number, type?: "pct" | "num"): React.ReactNode => {
    if (type === "pct") return <span className={styles.num}>%{value}</span>;
    return <span className={styles.num}>{value.toLocaleString("tr-TR")}</span>;
  };

  const renderLevel4Row = (u: Level4Item) => (
    <tr key={u.enrollmentId} className={styles.row4}>
      <td className={styles.indent4}>{u.fullName}</td>
      <td>{u.identityNumber ?? "—"}</td>
      <td>{u.title ?? "—"}</td>
      <td>
        {u.hasCertificate ? (
          <span className={styles.pillIssued}>
            {t("reports.certificateHierarchy.issued")}
          </span>
        ) : (
          <span className={styles.pillPending}>
            {t("reports.certificateHierarchy.pending")}
          </span>
        )}
      </td>
      <td>
        {u.certificateNumber ?? "—"}
      </td>
      <td>
        {u.issuedAt ? new Date(u.issuedAt).toLocaleDateString("tr-TR") : "—"}
      </td>
      <td>
        {u.examScorePercentage != null
          ? `%${Math.round(u.examScorePercentage)}`
          : "—"}
      </td>
    </tr>
  );

  const renderLevel3Row = (
    certId: string,
    eduId: string,
    d: Level3Item,
  ): React.ReactNode => {
    const rowKey = `L3:${certId}:${eduId}:${d.departmentId}`;
    const cacheKey: LevelKey = `L3:${certId}:${eduId}:${d.departmentId}`;
    const isOpen = expanded.has(rowKey);
    const childState = children.get(cacheKey);
    return (
      <React.Fragment key={rowKey}>
        <tr
          className={`${styles.row3} ${isOpen ? styles.rowOpen : ""}`}
          onClick={() =>
            toggleRow(
              rowKey,
              () =>
                certificateHierarchyApi
                  .getLevel4(certId, eduId, d.departmentId, {
                    ...filters,
                    page: 1,
                    limit: 100,
                  })
                  .then((r) => r.items),
              cacheKey,
            )
          }
        >
          <td className={styles.indent3}>
            <ArrowDown
              width={12}
              height={12}
              className={`${styles.chev} ${isOpen ? styles.chevOpen : ""}`}
            />
            {d.departmentName}
          </td>
          <td>{renderMetricCell(d.totalEnrollments, "num")}</td>
          <td>{renderMetricCell(d.issuedCount, "num")}</td>
          <td>{renderMetricCell(d.pendingCount, "num")}</td>
          <td>{renderMetricCell(d.issueRatePercent, "pct")}</td>
          <td colSpan={2} />
        </tr>
        {isOpen && (
          <tr>
            <td colSpan={7} className={styles.subTableWrap}>
              {childState?.loading ? (
                <div className={styles.inlineLoader}>
                  {t("common.loading")}
                </div>
              ) : (childState?.items as Level4Item[])?.length ? (
                <table className={styles.subTable}>
                  <thead>
                    <tr>
                      <th>
                        {t("reports.certificateHierarchy.user")}
                      </th>
                      <th>TC</th>
                      <th>
                        {t("reports.certificateHierarchy.titleCol")}
                      </th>
                      <th>
                        {t("reports.certificateHierarchy.certStatus")}
                      </th>
                      <th>
                        {t("reports.certificateHierarchy.certNumber")}
                      </th>
                      <th>
                        {t("reports.certificateHierarchy.issuedAt")}
                      </th>
                      <th>
                        {t("reports.certificateHierarchy.examScore")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(childState?.items as Level4Item[]).map(renderLevel4Row)}
                  </tbody>
                </table>
              ) : (
                <div className={styles.empty}>
                  {t("reports.certificateHierarchy.noUsers")}
                </div>
              )}
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  const renderLevel2Row = (
    certId: string,
    e: Level2Item,
  ): React.ReactNode => {
    const rowKey = `L2:${certId}:${e.educationId}`;
    const cacheKey: LevelKey = `L2:${certId}:${e.educationId}`;
    const isOpen = expanded.has(rowKey);
    const childState = children.get(cacheKey);
    return (
      <React.Fragment key={rowKey}>
        <tr
          className={`${styles.row2} ${isOpen ? styles.rowOpen : ""}`}
          onClick={() =>
            toggleRow(
              rowKey,
              () =>
                certificateHierarchyApi
                  .getLevel3(certId, e.educationId, {
                    ...filters,
                    page: 1,
                    limit: 100,
                  })
                  .then((r) => r.items),
              cacheKey,
            )
          }
        >
          <td className={styles.indent2}>
            <ArrowDown
              width={14}
              height={14}
              className={`${styles.chev} ${isOpen ? styles.chevOpen : ""}`}
            />
            <span className={styles.primaryName}>{e.educationName}</span>
            {e.code && <span className={styles.codeTag}>{e.code}</span>}
          </td>
          <td>{renderMetricCell(e.totalEnrollments, "num")}</td>
          <td>{renderMetricCell(e.issuedCount, "num")}</td>
          <td>{renderMetricCell(e.pendingCount, "num")}</td>
          <td>{renderMetricCell(e.issueRatePercent, "pct")}</td>
          <td colSpan={2} />
        </tr>
        {isOpen && (
          <tr>
            <td colSpan={7} className={styles.subTableWrap}>
              {childState?.loading ? (
                <div className={styles.inlineLoader}>
                  {t("common.loading")}
                </div>
              ) : (childState?.items as Level3Item[])?.length ? (
                <table className={styles.subTable}>
                  <thead>
                    <tr>
                      <th>
                        {t("reports.certificateHierarchy.department")}
                      </th>
                      <th>
                        {t("reports.certificateHierarchy.total")}
                      </th>
                      <th>
                        {t("reports.certificateHierarchy.issued")}
                      </th>
                      <th>
                        {t("reports.certificateHierarchy.pending")}
                      </th>
                      <th>
                        {t("reports.certificateHierarchy.rate")}
                      </th>
                      <th colSpan={2} />
                    </tr>
                  </thead>
                  <tbody>
                    {(childState?.items as Level3Item[]).map((d) =>
                      renderLevel3Row(certId, e.educationId, d),
                    )}
                  </tbody>
                </table>
              ) : (
                <div className={styles.empty}>
                  {t("reports.certificateHierarchy.noDepartments")}
                </div>
              )}
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  const renderLevel1Row = (c: Level1Item): React.ReactNode => {
    const rowKey = `L1:${c.certificateId}`;
    const cacheKey: LevelKey = `L1:${c.certificateId}`;
    const isOpen = expanded.has(rowKey);
    const childState = children.get(cacheKey);
    return (
      <React.Fragment key={rowKey}>
        <tr
          className={`${styles.row1} ${isOpen ? styles.rowOpen : ""}`}
          onClick={() =>
            toggleRow(
              rowKey,
              () =>
                certificateHierarchyApi
                  .getLevel2(c.certificateId, {
                    ...filters,
                    page: 1,
                    limit: 100,
                  })
                  .then((r) => r.items),
              cacheKey,
            )
          }
        >
          <td className={styles.indent1}>
            <ArrowDown
              width={16}
              height={16}
              className={`${styles.chev} ${isOpen ? styles.chevOpen : ""}`}
            />
            <span className={styles.primaryName}>{c.certificateName}</span>
            <span className={styles.pillCount}>
              {c.educationsCount}{" "}
              {t("reports.certificateHierarchy.educationsSuffix")}
            </span>
          </td>
          <td>{renderMetricCell(c.totalEnrollments, "num")}</td>
          <td>{renderMetricCell(c.issuedCount, "num")}</td>
          <td>{renderMetricCell(c.pendingCount, "num")}</td>
          <td>{renderMetricCell(c.issueRatePercent, "pct")}</td>
          <td colSpan={2} />
        </tr>
        {isOpen && (
          <tr>
            <td colSpan={7} className={styles.subTableWrap}>
              {childState?.loading ? (
                <div className={styles.inlineLoader}>
                  {t("common.loading")}
                </div>
              ) : (childState?.items as Level2Item[])?.length ? (
                <table className={styles.subTable}>
                  <thead>
                    <tr>
                      <th>
                        {t("reports.certificateHierarchy.education")}
                      </th>
                      <th>
                        {t("reports.certificateHierarchy.total")}
                      </th>
                      <th>
                        {t("reports.certificateHierarchy.issued")}
                      </th>
                      <th>
                        {t("reports.certificateHierarchy.pending")}
                      </th>
                      <th>
                        {t("reports.certificateHierarchy.rate")}
                      </th>
                      <th colSpan={2} />
                    </tr>
                  </thead>
                  <tbody>
                    {(childState?.items as Level2Item[]).map((e) =>
                      renderLevel2Row(c.certificateId, e),
                    )}
                  </tbody>
                </table>
              ) : (
                <div className={styles.empty}>
                  {t("reports.certificateHierarchy.noEducations")}
                </div>
              )}
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  const totals = useMemo(() => {
    return level1.reduce(
      (acc, c) => ({
        total: acc.total + c.totalEnrollments,
        issued: acc.issued + c.issuedCount,
        pending: acc.pending + c.pendingCount,
      }),
      { total: 0, issued: 0, pending: 0 },
    );
  }, [level1]);

  const aggregateRate = totals.total
    ? Math.round((totals.issued / totals.total) * 100)
    : 0;

  return (
    <DashboardLayout>
      <Header
        title={t("reports.certificateHierarchy.title")}
        description={t("reports.certificateHierarchy.description")}
      />

      <div className={styles.container}>
        {/* Summary strip */}
        <div className={styles.summary}>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>
              {t("reports.certificateHierarchy.summary.total")}
            </span>
            <span className={styles.summaryValue}>
              {totals.total.toLocaleString("tr-TR")}
            </span>
          </div>
          <div className={`${styles.summaryCard} ${styles.summarySuccess}`}>
            <span className={styles.summaryLabel}>
              {t("reports.certificateHierarchy.summary.issued")}
            </span>
            <span className={styles.summaryValue}>
              {totals.issued.toLocaleString("tr-TR")}
            </span>
          </div>
          <div className={`${styles.summaryCard} ${styles.summaryWarning}`}>
            <span className={styles.summaryLabel}>
              {t("reports.certificateHierarchy.summary.pending")}
            </span>
            <span className={styles.summaryValue}>
              {totals.pending.toLocaleString("tr-TR")}
            </span>
          </div>
          <div className={`${styles.summaryCard} ${styles.summaryAccent}`}>
            <span className={styles.summaryLabel}>
              {t("reports.certificateHierarchy.summary.rate")}
            </span>
            <span className={styles.summaryValue}>%{aggregateRate}</span>
          </div>
        </div>

        {renderFilters()}

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.nameCol}>
                  {t("reports.certificateHierarchy.certificate")}
                </th>
                <th>
                  {t("reports.certificateHierarchy.total")}
                </th>
                <th>
                  {t("reports.certificateHierarchy.issued")}
                </th>
                <th>
                  {t("reports.certificateHierarchy.pending")}
                </th>
                <th>{t("reports.certificateHierarchy.rate")}</th>
                <th colSpan={2} />
              </tr>
            </thead>
            <tbody>
              {level1Loading ? (
                <tr>
                  <td colSpan={7} className={styles.inlineLoader}>
                    {t("common.loading")}
                  </td>
                </tr>
              ) : level1.length === 0 ? (
                <tr>
                  <td colSpan={7} className={styles.empty}>
                    {t("reports.certificateHierarchy.noCertificates")}
                  </td>
                </tr>
              ) : (
                level1.map(renderLevel1Row)
              )}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CertificateHierarchyReportPage;
